import type { QueryKey } from '@tanstack/react-query'
import { NETWORK_CONSTANTS, UI_CONSTANTS } from '@/lib/constants'
import { isTransientInfrastructureError } from '@/lib/errors'

function liveQueryRetryDelay(attemptIndex: number): number {
  return Math.min(
    NETWORK_CONSTANTS.QUERY_RETRY_BASE_DELAY_MS * 2 ** attemptIndex,
    NETWORK_CONSTANTS.QUERY_RETRY_MAX_DELAY_MS
  )
}

function queryShouldRetry(failureCount: number, error: unknown, maxAttempts: number): boolean {
  return isTransientInfrastructureError(error) && failureCount < maxAttempts
}

function liveQueryShouldRetry(failureCount: number, error: unknown): boolean {
  return queryShouldRetry(failureCount, error, NETWORK_CONSTANTS.QUERY_RETRY_MAX_ATTEMPTS)
}

function staticQueryShouldRetry(failureCount: number, error: unknown): boolean {
  return queryShouldRetry(
    failureCount,
    error,
    NETWORK_CONSTANTS.QUERY_RETRY_MAX_ATTEMPTS_STATIC
  )
}

/**
 * TanStack Query cache policy for clinic-ms.
 *
 * Uses in-memory caching only. Does not persist PHI or consultation drafts to
 * disk (see remote `consultation_draft_caches` and project notebook policy).
 * Mutations still invalidate affected keys so cached reads stay correct.
 */

/** Default freshness for most entity lists and details. */
export const QUERY_STANDARD_STALE_MS = UI_CONSTANTS.DEFAULT_STALE_TIME

/** Catalog / config that changes rarely (drug list metadata, surgery types, departments). */
export const QUERY_STATIC_STALE_MS = 30 * 60 * 1000

/** Boards and same-day operational metrics (queue, dashboard tiles). */
export const QUERY_LIVE_STALE_MS = 60 * 1000

/** Retain unused query data in memory for fast back-navigation within a session. */
export const QUERY_GC_TIME_MS = 30 * 60 * 1000

const QUERY_ROOT = (queryKey: QueryKey): string => String(queryKey[0] ?? '')

/** Roots that must stay in-memory only (no disk persistence if added later). */
const IN_MEMORY_ONLY_ROOTS = new Set([
  'consultationDraftCache',
  'consultation',
  'examination',
  'visualAcuity',
  'prescription',
  'patients',
  'patientVisits',
  'patientConsultations',
  'patient',
  'queue',
  'queueConsultations',
  'queueExaminations',
  'queueVisualAcuities',
  'queue-admission-availability',
  'notifications',
  'dashboard',
  'transactions',
  'invoices',
  'reports',
  'finance',
  'dispensary',
  'visit',
  'visits',
  'staff-schedule',
])

export function getQueryCacheRoot(queryKey: QueryKey): string {
  return QUERY_ROOT(queryKey)
}

/**
 * Whether a query may ever be written to browser storage.
 * Today the app does not use a persist client; this guard documents intent.
 */
export function isQueryAllowedOnDisk(queryKey: QueryKey): boolean {
  return !IN_MEMORY_ONLY_ROOTS.has(getQueryCacheRoot(queryKey))
}

export const defaultQueryClientOptions = {
  queries: {
    staleTime: QUERY_STANDARD_STALE_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    networkMode: 'offlineFirst' as const,
    retry: liveQueryShouldRetry,
    retryDelay: liveQueryRetryDelay,
  },
} as const

/** Rarely changing reference data. */
export const staticDataQueryOptions = {
  staleTime: QUERY_STATIC_STALE_MS,
  gcTime: QUERY_GC_TIME_MS,
  networkMode: 'offlineFirst' as const,
  retry: staticQueryShouldRetry,
  retryDelay: liveQueryRetryDelay,
} as const

/** Frequently changing operational views. */
export const liveDataQueryOptions = {
  staleTime: QUERY_LIVE_STALE_MS,
  gcTime: QUERY_GC_TIME_MS,
  networkMode: 'offlineFirst' as const,
  retry: liveQueryShouldRetry,
  retryDelay: liveQueryRetryDelay,
} as const
